import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { smsActionConfigsApi } from '@/lib/api/sms-action-configs.api';

import type {
  SmsActionConfig,
  CreateSmsActionConfigDto,
  UpdateSmsActionConfigDto,
} from '@repo/shared-types';

export function useSmsActionConfigs() {
  return useQuery<SmsActionConfig[]>({
    queryKey: ['sms-action-configs'],
    queryFn: () => smsActionConfigsApi.getAll(),
  });
}

export function useSmsActionConfig(id: string | undefined) {
  return useQuery<SmsActionConfig>({
    queryKey: ['sms-action-configs', id],
    queryFn: () => smsActionConfigsApi.getOne(id!),
    enabled: !!id,
  });
}

export function useCreateSmsActionConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSmsActionConfigDto) => smsActionConfigsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms-action-configs'] });
    },
  });
}

export function useUpdateSmsActionConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSmsActionConfigDto }) =>
      smsActionConfigsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms-action-configs'] });
    },
  });
}

export function useDeleteSmsActionConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => smsActionConfigsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms-action-configs'] });
    },
  });
}

export function useSmsActionLogs(configId: string | undefined, page = 1, limit = 50) {
  return useQuery({
    queryKey: ['sms-action-logs', configId, page, limit],
    queryFn: () => smsActionConfigsApi.getLogs(configId!, page, limit),
    enabled: !!configId,
  });
}
